import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY,
  FIRM_VALUATIONS_DEFINITION_DATA_QUERY_KEY,
} from 'common/constants/services/firms';
import { useStore } from 'common/store';
import { OrderedDefinition, useSummaryDefinitions } from 'context/SummaryDefinitionsContext';
import { useAIAssistSummary, useAIAssistValuationsSummary } from 'services/hooks/summaryDefinitions';
import { getObjectValue } from 'utilities';

const AI_ASSIST_ERROR_MESSAGE = 'AI assistant is temporarily unavailable. Please try again.';

export interface UseSummaryAIAssistParams {
  // The definition the URL is showing. Prefer the workspace's slug-derived definition over the
  // context's activeDefinition: the two are synced by an effect, so activeDefinition lags by a
  // render immediately after a tab switch.
  definition: OrderedDefinition | null;
  summaryType?: 'firm' | 'valuations';
}

export interface UseSummaryAIAssistResult {
  // No definition, or one with no spreadsheet payload to revise.
  isDisabled: boolean;
  // Spans the whole round trip — generate, persist, then reload the grid. Deliberately wider than
  // the mutation's own isPending, which resolves while the sheet is still refetching.
  isSubmitting: boolean;
  // Resolves true only once a revision is fully applied, so the caller clears its input exactly then.
  submitPrompt: (prompt: string) => Promise<boolean>;
}

// Owns the summary AI assistant round trip so the control that triggers it can live in the tertiary
// navigation bar, which is registered above the component tree that renders the sheet.
export const useSummaryAIAssist = ({
  definition,
  summaryType,
}: UseSummaryAIAssistParams): UseSummaryAIAssistResult => {
  const isValuations = summaryType === 'valuations';
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { refetch, updateDefinition } = useSummaryDefinitions();

  const [storeValue] = useStore();
  const currentFirmId = getObjectValue(getObjectValue(storeValue).firmInfo).id;

  // Depend on the mutation functions alone: the mutation result objects are rebuilt on every
  // render, and a `submitPrompt` that changed identity each render would re-register the tertiary
  // navigation control (which compares `onSubmit` by reference) in a loop.
  const { mutateAsync: aiAssistFirm } = useAIAssistSummary(currentFirmId);
  const { mutateAsync: aiAssistValuations } = useAIAssistValuationsSummary(currentFirmId);
  const aiAssist = isValuations ? aiAssistValuations : aiAssistFirm;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const spreadsheetDefinition = definition?.spreadsheet_definition ?? definition?.spreadsheetDefinition;

  const submitPrompt = useCallback(
    async (prompt: string): Promise<boolean> => {
      if (!definition || !spreadsheetDefinition) {
        return false;
      }

      setIsSubmitting(true);
      try {
        const result = await aiAssist({
          prompt,
          current_definition: spreadsheetDefinition,
        });

        await updateDefinition(definition.id, {
          spreadsheet_definition: result.spreadsheet_definition,
        });

        const definitionDataQueryKey = isValuations
          ? FIRM_VALUATIONS_DEFINITION_DATA_QUERY_KEY
          : FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY;
        await Promise.all([refetch(), queryClient.invalidateQueries({ queryKey: [definitionDataQueryKey] })]);

        if (result.message) {
          enqueueSnackbar(result.message, { variant: 'success' });
        }

        return true;
      } catch {
        enqueueSnackbar(AI_ASSIST_ERROR_MESSAGE, { variant: 'error' });
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      aiAssist,
      definition,
      enqueueSnackbar,
      isValuations,
      queryClient,
      refetch,
      spreadsheetDefinition,
      updateDefinition,
    ]
  );

  return {
    isDisabled: !definition || !spreadsheetDefinition,
    isSubmitting,
    submitPrompt,
  };
};

export default useSummaryAIAssist;
